<template>
  <DataGridTable>
    <template #header>
      <DataGridHead>
        <DataGridHeadCell label="Name" />
        <DataGridHeadCell label="Status" align="center" />
        <DataGridHeadCell label="Collection" />
        <DataGridHeadCell label="Custom ID" />
        <DataGridHeadCell label="Created" />
        <DataGridHeadCell label="Expires" />
        <DataGridHeadCell label="Actions" align="right" />
      </DataGridHead>
    </template>

    <DataGridBody>
      <!-- Show skeleton loaders when loading -->
      <template v-if="isLoading">
        <RequestContentSkeletonRow v-for="i in dynamicSkeletonCount" :key="`skeleton-${i}`" />
      </template>
      <!-- Show empty state when no data and not loading -->
      <DataGridEmptyRow v-else-if="list.length === 0" :column-count="7">
        <template #icon>
          <Icon name="general.plus" size="xl" class="w-12 h-12 mx-auto text-zinc-400 dark:text-zinc-500" />
        </template>
        <template #title>No request links yet</template>
        <template #description>Create your first request link to start collecting videos from others.</template>
      </DataGridEmptyRow>
      <!-- Show actual data rows -->
      <template v-else>
        <RequestContentRow v-for="item in list" :key="item.id" :item="item" @edit="$emit('edit', item)" @delete="$emit('delete', item)" />
      </template>
    </DataGridBody>

    <template #footer>
      <DataGridPagination
        v-if="showPagination"
        :current-page="currentPage"
        :total-pages="totalPages"
        :total-items="totalItems"
        :per-page="perPage"
        mode="full"
        @page-change="handlePageChange"
      />
    </template>
  </DataGridTable>
</template>

<script setup lang="ts">
import { computed, nextTick, watch } from 'vue';
import { DataGridTable, DataGridHead, DataGridHeadCell, DataGridBody, DataGridEmptyRow, DataGridPagination } from '@/storybook/datagrid';
import { Icon } from '@/storybook';
import type { RequestContent } from '../../types';
import RequestContentRow from './RequestContentRow.vue';
import RequestContentSkeletonRow from '../shared/RequestContentSkeletonRow.vue';
import { useSkeletonLoader } from '../../composables/useSkeletonLoader';

interface Props {
  list: RequestContent[];
  domain: string;
  isLoading?: boolean;
  // Pagination props
  currentPage?: number;
  totalPages?: number;
  totalItems?: number;
  perPage?: number;
}

const props = withDefaults(defineProps<Props>(), {
  isLoading: false,
  currentPage: 1,
  totalPages: 1,
  totalItems: 0,
  perPage: 10,
});

const emit = defineEmits<{
  edit: [item: RequestContent];
  delete: [item: RequestContent];
  'page-change': [page: number];
}>();

// Skeleton loader composable
const { dynamicSkeletonCount, preserveRowCount, resetRowCount } = useSkeletonLoader(5);

const showPagination = computed(() => {
  return !props.isLoading && props.totalPages > 1;
});

// Handle page change with row count preservation
const handlePageChange = (page: number) => {
  // Store the current number of visible rows before loading starts
  preserveRowCount(props.list.length);
  emit('page-change', page);
};

// Reset preserved count after content is loaded
watch(
  () => props.isLoading,
  async isLoading => {
    if (!isLoading) {
      // Wait for content to render
      await nextTick();
      setTimeout(() => {
        resetRowCount();
      }, 50);
    }
  }
);
</script>
